import {
  differenceInDays,
  format,
  isSameDay,
  parseISO,
  subDays,
} from 'date-fns'

import Checkbox from 'common/components/checkbox'
import DatePicker from 'common/components/datePicker'
import FormItem from 'common/components/formItem'
import TimePicker from 'common/components/timePicker'
import {
  DataCollection as DataCollectionIcon,
  Time as TimeIcon,
} from 'common/icons'

import SelectComponent from '../select/SelectComponent'

import { Props, SchedulerDate, SchedulerTime } from './interface'

import styles from 'workflow/CampaignSummary/IntegrationOptions/Integrations.module.css'

const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

/** Returns today's date string as 'yyyy-MM-dd' in the given timezone */
const getTzDateString = (tz: string): string =>
  new Intl.DateTimeFormat('sv-SE', { timeZone: tz }).format(new Date())

/** Returns current hour (0–23) in the given timezone */
const getTzHour = (tz: string): number =>
  parseInt(
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      hour12: false,
    }).format(new Date()),
    10
  )

/** Returns current minute (0–59) */
const getTzMinute = (): number => new Date().getMinutes()

export default function Scheduler({
  icon,
  title,
  startTimeTitle,
  endTimeTitle,
  isStartDateDisabled,
  isStartTimeDisabled,
  isEndDateDisabled,
  isEndTimeDisabled,
  startDate,
  startTime,
  endDate,
  endTime,
  minuteStep,
  onChange,
  integrationType,
  errorMessages,
  timeZone = defaultTimezone,
  testLength,
  autoDeploy,
  delayInDaysToRetrieveTheFinalResult,
  delayInHourToRetrieveSplitResult,
}: Props) {
  const disableDate = ({
    current,
    startDate,
    compareStartDate = false,
    timeZone,
    delayInDaysToRetrieveTheFinalResult = 0,
  }: {
    current: Date
    startDate: SchedulerDate
    compareStartDate?: boolean
    timeZone: string
    delayInDaysToRetrieveTheFinalResult?: number
  }): boolean => {
    const dateToCompare =
      compareStartDate && startDate
        ? format(startDate, 'yyyy-MM-dd')
        : getTzDateString(timeZone)

    if (delayInDaysToRetrieveTheFinalResult) {
      return (
        format(
          subDays(current, delayInDaysToRetrieveTheFinalResult),
          'yyyy-MM-dd'
        ) < dateToCompare
      )
    }
    return format(current, 'yyyy-MM-dd') < dateToCompare
  }

  const getDisabledHoursArray = (
    dateFormatted: string,
    timeFormatted: string,
    extraTime: number = 3600
  ): number[] => {
    const dt = parseISO(`${dateFormatted}T${timeFormatted}:00`)
    const adjusted = new Date(dt.getTime() + extraTime * 1000)
    return Array.from(Array(adjusted.getHours()).keys())
  }

  const disableStartHours = ({
    startDate,
    timeZone,
  }: {
    startDate: SchedulerDate
    timeZone: string
  }): number[] => {
    const todayTz = getTzDateString(timeZone)
    const startDateStr = startDate ? format(startDate, 'yyyy-MM-dd') : ''

    if (
      timeZone &&
      startDate &&
      startDateStr !== todayTz &&
      startDateStr > todayTz
    ) {
      return []
    }
    if (startDate && format(startDate, 'yyyy-MM-dd') === todayTz) {
      const dateFormatted = getTzDateString(timeZone)
      const hour = getTzHour(timeZone)
      const minute = getTzMinute()
      const timeFormatted = `${String(hour).padStart(2, '0')}:${String(
        minute
      ).padStart(2, '0')}`
      return getDisabledHoursArray(
        dateFormatted,
        timeFormatted,
        delayInHourToRetrieveSplitResult
      )
    }
    return Array.from(Array(new Date().getDate() + 1).keys())
  }

  const disableStartMinutes = ({
    startDate,
    selectedHour,
    timeZone,
  }: {
    startDate: SchedulerDate
    selectedHour: number
    timeZone: string
  }): number[] => {
    const todayTz = getTzDateString(timeZone)
    const startDateStr = startDate ? format(startDate, 'yyyy-MM-dd') : ''

    if (startDate && startDateStr > todayTz) {
      return []
    }
    const dateFormatted = getTzDateString(timeZone)
    const hour = getTzHour(timeZone)
    const minute = getTzMinute()
    const timeFormatted = `${String(hour).padStart(2, '0')}:${String(
      minute
    ).padStart(2, '0')}`
    return getDisabledMinutesArray(selectedHour, dateFormatted, timeFormatted)
  }

  const getDisabledMinutesArray = (
    selectedHour: number,
    dateFormatted: string,
    timeFormatted: string
  ): number[] => {
    const extraTime = 3600
    const dt = parseISO(`${dateFormatted}T${timeFormatted}:00`)
    const adjusted = new Date(dt.getTime() + extraTime * 1000)
    const minutesIncludingExtraTime = adjusted.getMinutes()
    const nowHourIncludingExtraTime = adjusted.getHours()

    if (selectedHour && selectedHour - nowHourIncludingExtraTime >= 1) {
      return []
    }
    return Array.from(Array(minutesIncludingExtraTime).keys())
  }

  const disableEndHours = ({
    startTime,
    endDate,
    startDate,
    delayInDaysToRetrieveTheFinalResult = 0,
  }: {
    startTime: SchedulerTime | undefined
    endDate: SchedulerDate
    startDate: SchedulerDate
    delayInDaysToRetrieveTheFinalResult?: number
  }): number[] => {
    if (!startDate || !endDate || !startTime) {
      return []
    }
    const dateFormatted = format(startDate, 'yyyy-MM-dd')
    const timeFormatted = startTime
    if (
      isSameDay(startDate, endDate) ||
      (delayInDaysToRetrieveTheFinalResult &&
        differenceInDays(endDate, startDate) ===
          delayInDaysToRetrieveTheFinalResult)
    ) {
      return getDisabledHoursArray(dateFormatted, timeFormatted)
    }
    return []
  }

  const disableEndMinutes = ({
    selectedHour,
    startDate,
    startTime,
    endDate,
  }: {
    selectedHour: number
    startDate: SchedulerDate
    startTime: SchedulerTime | undefined
    endDate: SchedulerDate
  }): number[] => {
    if (!startDate || !endDate || !startTime) {
      return []
    }
    const dateFormatted = format(startDate, 'yyyy-MM-dd')
    const timeFormatted = startTime
    if (isSameDay(startDate, endDate)) {
      return getDisabledMinutesArray(selectedHour, dateFormatted, timeFormatted)
    }
    return []
  }

  return (
    <div className="mt-6 mb-4 bg-coolGray-50 p-4 border border-gray-200 updated-component">
      <div className="flex text-lg items-center mb-4">
        {icon ? icon : <DataCollectionIcon />}
        <div className="font-bold ml-4">{title}</div>
      </div>
      <FormItem
        label={startTimeTitle}
        error={errorMessages?.startDate || errorMessages?.startTime}
      >
        <div className="flex justify-between">
          <DatePicker
            id="date-picker"
            numberOfMonths={1}
            verticalHeight={372}
            isTodayVisible={true}
            date={startDate}
            showClearDate={false}
            placeholder="Date"
            disabled={isStartDateDisabled}
            data-testid="startDateTestId"
            onDateChange={(val: Date | null) => onChange(val, 'startDate')}
            isOutsideRange={(current: Date): boolean =>
              disableDate({
                current,
                startDate,
                compareStartDate: false,
                timeZone,
              })
            }
          />
          <TimePicker
            value={startTime ?? null}
            allowClear={false}
            format={undefined}
            minuteStep={minuteStep}
            style={{ width: '100%' }}
            disabled={isStartTimeDisabled}
            data-testid="startTimeTestId"
            className={styles.integrationTimePicker}
            onChange={(val: string | null) => onChange(val ?? '', 'startTime')}
            disabledHours={() => disableStartHours({ startDate, timeZone })}
            disabledMinutes={(selectedHour: number) =>
              disableStartMinutes({ selectedHour, startDate, timeZone })
            }
            suffixIcon={<TimeIcon size={6} />}
          />
        </div>
      </FormItem>

      {testLength && (
        <SelectComponent
          className="mb-6"
          dataTestId="select-test-length"
          isSelectDisabled={testLength.isDisabled}
          labelTitle="How long will the test run?"
          placeholder="Select wait hours"
          selectOptions={testLength.options}
          onChange={(val) => testLength.onChange(val)}
          selectValue={testLength.value}
          errorMessage={testLength.errorMessage}
        />
      )}

      <FormItem
        label={endTimeTitle}
        error={errorMessages?.endDate || errorMessages?.endTime}
      >
        <div className="flex justify-between">
          <DatePicker
            id="date-picker"
            numberOfMonths={1}
            verticalHeight={372}
            isTodayVisible={true}
            date={endDate ?? null}
            showClearDate={false}
            placeholder="Date"
            disabled={isEndDateDisabled}
            data-testid="endDateTestId"
            onDateChange={(val: Date | null) => onChange(val, 'endDate')}
            isOutsideRange={(current: Date): boolean =>
              disableDate({
                current,
                startDate,
                compareStartDate: true,
                timeZone,
                delayInDaysToRetrieveTheFinalResult,
              })
            }
          />
          <TimePicker
            value={endTime ?? null}
            allowClear={false}
            minuteStep={minuteStep}
            style={{ width: '100%' }}
            disabled={isEndTimeDisabled}
            data-testid="endTimeTestId"
            className={styles.integrationTimePicker}
            onChange={(val: string | null) => val && onChange(val, 'endTime')}
            disabledHours={() =>
              disableEndHours({
                startTime,
                endDate: endDate ?? null,
                startDate,
                delayInDaysToRetrieveTheFinalResult,
              })
            }
            disabledMinutes={(selectedHour) =>
              disableEndMinutes({
                selectedHour,
                startDate,
                startTime,
                endDate: endDate ?? null,
              })
            }
            suffixIcon={<TimeIcon size={6} />}
          />
        </div>
      </FormItem>

      {timeZone && integrationType ? (
        <>
          <div className="mb-2 font-medium">Account Timezone: {timeZone}</div>
          <div className="mb-4 text-coolGray-400">
            {'Input your experiment time details based on your '}
            {integrationType}
            {' Account timezone.'}
          </div>
        </>
      ) : undefined}

      {autoDeploy && (
        <Checkbox
          className="mt-6"
          data-testid="auto-deploy"
          data-cy="auto-deploy-checkbox"
          label="Automatically deploy the winner?"
          isChecked={autoDeploy.checked}
          isDisabled={autoDeploy.isDisabled}
          onChange={autoDeploy.onChange}
        />
      )}
    </div>
  )
}
